import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import type { AccountInfo, AuthenticationResult, SilentRequest, PopupRequest } from '@azure/msal-browser';
import { verifyCode, requestCode } from '../../services/authDb';

// Types for user profile and authentication state
export interface UserProfile {
  id: string;
  email: string;
  name: string;
  roles: string[];
  department?: string;
  jobTitle?: string;
  avatar?: string;
}

export interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserProfile | null;
  apiToken: string | null; // Backend API token (aud = api://{clientId}/access_as_user)
  graphToken: string | null; // Microsoft Graph token
  // Connectivity & degraded mode
  connectivity?: 'online' | 'degraded';
  lastReachableAt?: number | null;
  retryConnectivityCheck?: () => void;
  lastKnownProfileCached?: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  acquireGraphTokenSilently: () => Promise<string | null>;
  acquireApiToken: () => Promise<string | null>;
  error: string | null;
  // DB provider helpers (no-ops in Azure mode)
  dbRequestCode?: (email: string) => Promise<{ message: string }>;
  dbVerifyAndLogin?: (email: string, code: string) => Promise<void>;
}

// Create the authentication context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_PROVIDER = (process.env.REACT_APP_AUTH_PROVIDER || 'AZURE_AD').toUpperCase();
let msalInstance: any = null; // initialized lazily for Azure

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [apiToken, setApiToken] = useState<string | null>(null);
  const [graphToken, setGraphToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connectivity, setConnectivity] = useState<'online' | 'degraded'>('online');
  const [lastReachableAt, setLastReachableAt] = useState<number | null>(null);
  const backoffRef = useRef<number>(2000); // ms
  const healthTimerRef = useRef<number | null>(null);
  const [lastKnownProfileCached, setLastKnownProfileCached] = useState<boolean>(false);
  const connectivityRef = useRef<'online' | 'degraded'>(connectivity);
  const lastProfileSyncAtRef = useRef<number>(0);
  const profileSyncInFlightRef = useRef<boolean>(false);

  // Initialize MSAL and check for existing authentication (one-time guard for StrictMode)
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current) return; // guard against StrictMode double effect
    initRef.current = true;

    const initialize = async () => {
      try {
        if (AUTH_PROVIDER === 'DB') {
          // Restore DB token if present
          const stored = window.localStorage.getItem('db_access_token');
          const storedUser = window.localStorage.getItem('db_user');
          if (stored && storedUser) {
            setApiToken(stored);
            setUser(JSON.parse(storedUser));
            setIsAuthenticated(true);
          }
        } else {
          // Lazy import MSAL and config only for Azure provider
          const [{ PublicClientApplication }, { msalConfig }] = await Promise.all([
            import('@azure/msal-browser'),
            import('../../config/authConfig'),
          ]);
          msalInstance = new PublicClientApplication(msalConfig);
          await msalInstance.initialize();
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            const account = accounts[0] as AccountInfo;
            await handleAuthenticationSuccess(account);
          }
        }
      } catch (err) {
        console.error('Auth initialization failed:', err);
        setError('Failed to initialize authentication');
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
    // Listen to global API error events to flip to degraded mode
    const apiErrorHandler = (e: Event) => {
      try {
        const detail = (e as CustomEvent<{ status?: number }>).detail;
        if (!detail) return;
        const { status } = detail;
        if (typeof status !== 'number' || status >= 500) {
          // Network or server error => degraded
          if (connectivity !== 'degraded') setConnectivity('degraded');
          scheduleHealthCheck();
        }
      } catch {}
    };
    window.addEventListener('api-error', apiErrorHandler as EventListener);
    return () => {
      window.removeEventListener('api-error', apiErrorHandler as EventListener);
      if (healthTimerRef.current) window.clearTimeout(healthTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep a global hint for listeners that don't have context access
  useEffect(() => {
    (window as any).__connectivity = connectivity;
    connectivityRef.current = connectivity;
  }, [connectivity]);

  const scheduleHealthCheck = () => {
    if (healthTimerRef.current) return; // already scheduled
    healthTimerRef.current = window.setTimeout(async () => {
      healthTimerRef.current = null;
      await performHealthCheck();
    }, backoffRef.current) as unknown as number;
  };

  const performHealthCheck = async () => {
    try {
      const base = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';
      // Public health endpoint: /api/v1/health
      const res = await fetch(`${base}/health`, { method: 'GET', cache: 'no-store' });
      if (res.ok) {
        // Capture previous connectivity before switching to online
        const prevConnectivity = connectivityRef.current;
        setConnectivity('online');
        connectivityRef.current = 'online';
        setLastReachableAt(Date.now());
        backoffRef.current = 2000; // reset backoff
        // Refresh profile only when recovering from degraded OR if last sync > 5 minutes
        const now = Date.now();
        const shouldSyncAfterRecovery: boolean = (prevConnectivity === 'degraded');
        const tooOld: boolean = now - (lastProfileSyncAtRef.current || 0) > 5 * 60 * 1000;
        const minGapOk: boolean = now - (lastProfileSyncAtRef.current || 0) > 15 * 1000; // avoid rapid repeats
        if ((shouldSyncAfterRecovery || tooOld) && minGapOk && apiToken && user && !profileSyncInFlightRef.current) {
          try {
            profileSyncInFlightRef.current = true;
            await syncUserProfileWithBackend(user, apiToken);
            lastProfileSyncAtRef.current = now;
            setUser({ ...user });
          } finally {
            profileSyncInFlightRef.current = false;
          }
        }
        return;
      }
      // else keep trying with backoff
      backoffRef.current = Math.min(backoffRef.current * 2, 20000);
    } catch {
      backoffRef.current = Math.min(backoffRef.current * 2, 20000);
    } finally {
      if (connectivityRef.current === 'degraded') scheduleHealthCheck();
    }
  };

  const retryConnectivityCheck = async () => {
    // Clear any pending scheduled check
    if (healthTimerRef.current) {
      window.clearTimeout(healthTimerRef.current);
      healthTimerRef.current = null;
    }
    // Reset backoff and perform an immediate health check
    backoffRef.current = 2000;
    await performHealthCheck();
    // If we are back online, force a full reload to clear any cached/degraded state
    if (connectivity === 'online') {
      try {
        // Soft reload current route
        window.location.reload();
      } catch {
        // no-op
      }
    } else {
      // Still degraded: schedule next check with backoff
      scheduleHealthCheck();
    }
  };

  // Handle successful authentication
  const handleAuthenticationSuccess = async (account: AccountInfo) => {
    try {
      setIsLoading(true);
      
      // Acquire tokens separately
      const [apiAccessToken, msGraphToken] = await Promise.all([
        acquireApiToken(),
        acquireGraphTokenSilently(),
      ]);

      if (apiAccessToken) {
        // Fetch user profile from Microsoft Graph if available; fallback to minimal profile
        let userProfile: UserProfile;
        try {
          if (!msGraphToken) throw new Error('No Graph token');
          userProfile = await fetchUserProfile(msGraphToken);
          setGraphToken(msGraphToken);
        } catch {
          // Minimal profile from MSAL account if Graph token not available
          userProfile = {
            id: account.homeAccountId,
            email: account.username || '',
            name: account.name || '',
            roles: [],
          } as UserProfile;
        }

        // Sync user profile with backend using API token
        await syncUserProfileWithBackend(userProfile, apiAccessToken);
        try {
          // cache last-known profile for degraded display-only use
          window.localStorage.setItem('last_known_profile', JSON.stringify({ profile: userProfile, ts: Date.now() }));
          setLastKnownProfileCached(true);
        } catch {}
        setUser(userProfile);
        setApiToken(apiAccessToken);
        setIsAuthenticated(true);
        setError(null);
      }
    } catch (err) {
      console.error('Authentication success handling failed:', err);
      setError('Failed to complete authentication');
    } finally {
      setIsLoading(false);
    }
  };

  // Acquire token silently for Microsoft Graph API
  const acquireGraphTokenSilently = async (): Promise<string | null> => {
    try {
      if (AUTH_PROVIDER === 'DB') return null;
      const { loginRequest } = await import('../../config/authConfig');
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        return null;
      }

      const silentRequest: SilentRequest = {
        ...(loginRequest as any),
        account: accounts[0],
      };

      const response = await msalInstance.acquireTokenSilent(silentRequest);
      return response.accessToken;
    } catch (err) {
      console.error('Silent token acquisition failed:', err);
      
      // If silent acquisition fails, try interactive login
      try {
        const { loginRequest } = await import('../../config/authConfig');
        const response = await msalInstance.acquireTokenPopup(loginRequest as any);
        return response.accessToken;
      } catch (interactiveErr) {
        console.error('Interactive token acquisition failed:', interactiveErr);
        return null;
      }
    }
  };

  // Acquire token specifically for backend API calls
  const acquireApiToken = async (): Promise<string | null> => {
    try {
      if (AUTH_PROVIDER === 'DB') {
        return apiToken;
      }
      // starting API token acquisition
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        console.error('❌ No accounts found for API token acquisition');
        return null;
      }

      // accounts found
      const { apiRequest } = await import('../../config/authConfig');

      const apiSilentRequest: SilentRequest = {
        ...(apiRequest as any),
        account: accounts[0],
      };

      const response = await msalInstance.acquireTokenSilent(apiSilentRequest);
      // token acquired
      // Cache token in state
      setApiToken(response.accessToken);
      return response.accessToken;
    } catch (err) {
      console.error('❌ API token acquisition failed:', err);
      console.error('🔍 Error details:', {
        name: (err as any)?.name,
        message: (err as any)?.message,
        errorCode: (err as any)?.errorCode,
        errorMessage: (err as any)?.errorMessage
      });
      
      // If silent acquisition fails, try interactive login for API scope
      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) {
          return null;
        }
        
        const { apiRequest } = await import('../../config/authConfig');
        const response = await msalInstance.acquireTokenPopup({
          ...(apiRequest as any),
          account: accounts[0],
        });
        setApiToken(response.accessToken);
        return response.accessToken;
      } catch (interactiveErr) {
        console.error('Interactive API token acquisition failed:', interactiveErr);
        return null;
      }
    }
  };

  // Fetch user profile from Microsoft Graph
  const fetchUserProfile = async (accessToken: string): Promise<UserProfile> => {
    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const graphUser = await response.json();
      
      return {
        id: graphUser.id,
        email: graphUser.mail || graphUser.userPrincipalName,
        name: graphUser.displayName,
        roles: [], // Will be populated from backend
        department: graphUser.department,
        jobTitle: graphUser.jobTitle,
        avatar: `https://graph.microsoft.com/v1.0/me/photo/$value`,
      };
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
      throw err;
    }
  };

  // Sync and enrich user profile with backend (fetch roles, etc.)
  const syncUserProfileWithBackend = async (userProfile: UserProfile, apiAccessToken: string) => {
    try {
      const base = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';
      const etagKey = 'profile_etag';
      let etag: string | null = null;
      try { etag = window.localStorage.getItem(etagKey); } catch {}

      const headers: Record<string, string> = {
        Authorization: `Bearer ${apiAccessToken}`,
        'Cache-Control': 'no-store',
      };
      // Bypass ETag on the very first sync to avoid stale roles after admin changes
      const firstSync = !lastProfileSyncAtRef.current;
      if (!firstSync && etag) headers['If-None-Match'] = etag;

      const response = await fetch(`${base}/auth/profile`, { headers });

      if (response.status === 304) {
        // Not modified; trust cached profile values in memory/localStorage
        try {
          const cached = window.localStorage.getItem('last_known_profile');
          if (cached) {
            const parsed = JSON.parse(cached);
            const cp = parsed.profile as UserProfile;
            // Merge cached roles and profile fields into current userProfile
            userProfile.roles = cp.roles || userProfile.roles || [];
            userProfile.department = cp.department ?? userProfile.department;
            userProfile.jobTitle = cp.jobTitle ?? userProfile.jobTitle;
            userProfile.avatar = cp.avatar ?? userProfile.avatar;
            setUser({ ...userProfile });
          }
        } catch {}
        lastProfileSyncAtRef.current = Date.now();
        if (connectivity !== 'online') setConnectivity('online');
        setLastReachableAt(Date.now());
        return;
      }

      if (response.ok) {
        const backendProfile = await response.json();
        // Update user profile with backend data (roles, permissions, etc.)
        userProfile.roles = backendProfile.roles || [];
        userProfile.department = backendProfile.department ?? userProfile.department;
        userProfile.jobTitle = backendProfile.job_title ?? userProfile.jobTitle;
        userProfile.avatar = backendProfile.avatar_url ?? userProfile.avatar;
        lastProfileSyncAtRef.current = Date.now();
        // Save last known on successful fetch
        try {
          window.localStorage.setItem('last_known_profile', JSON.stringify({ profile: userProfile, ts: Date.now() }));
          const newEtag = response.headers.get('ETag');
          if (newEtag) window.localStorage.setItem(etagKey, newEtag);
          setLastKnownProfileCached(true);
        } catch {}
        if (connectivity !== 'online') setConnectivity('online');
        setLastReachableAt(Date.now());
      }
    } catch (err) {
      console.error('Failed to sync user profile with backend:', err);
      // Flip into degraded if network/5xx suspected; fallback to cached display profile
      try {
        const cached = window.localStorage.getItem('last_known_profile');
        if (cached && !user) {
          const parsed = JSON.parse(cached);
          setUser(parsed.profile as UserProfile);
          setLastKnownProfileCached(true);
        }
      } catch {}
      if (connectivity !== 'degraded') setConnectivity('degraded');
      scheduleHealthCheck();
    }
  };

  // Login function
  const login = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      if (AUTH_PROVIDER === 'DB') {
        // For DB auth, LoginPage drives the flow. Nothing to do here.
        return;
      }
      const response: AuthenticationResult = await msalInstance.loginPopup({
        scopes: ['openid', 'profile', 'email', 'User.Read'],
        prompt: 'select_account',
      } as PopupRequest);
      if (response.account) await handleAuthenticationSuccess(response.account as AccountInfo);
    } catch (err: any) {
      console.error('Login failed:', err);
      setError(err.message || 'Login failed');
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);
      
      if (AUTH_PROVIDER === 'DB') {
        // Best-effort server-side blacklist of the current token
        try {
          const base = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';
          if (apiToken) {
            await fetch(`${base}/auth/db/logout`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${apiToken}` },
            });
          }
        } catch (err) {
          console.warn('DB logout (blacklist) failed, proceeding to clear client state:', err);
        }
        // Clear client-side state/storage either way
        window.localStorage.removeItem('db_access_token');
        window.localStorage.removeItem('db_user');
      } else {
        // Clear backend session
        if (apiToken) {
          try {
            await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'}/auth/logout`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${apiToken}` },
            });
          } catch (err) {
            console.error('Backend logout failed:', err);
          }
        }
        // Clear MSAL session via redirect (recommended for SSO sign-out)
        const { msalConfig } = await import('../../config/authConfig');
        await msalInstance.logoutRedirect({ postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri });
      }
      
      // Clear local state
      setIsAuthenticated(false);
      setUser(null);
      setApiToken(null);
      setGraphToken(null);
      setError(null);
    } catch (err: any) {
      console.error('Logout failed:', err);
      setError(err.message || 'Logout failed');
    } finally {
      setIsLoading(false);
    }
  };

  // DB provider helpers
  const dbRequestCode = async (email: string) => {
    if (AUTH_PROVIDER !== 'DB') return { message: '' };
    return await requestCode({ email });
  };

  const dbVerifyAndLogin = async (email: string, code: string) => {
    if (AUTH_PROVIDER !== 'DB') return;
    setIsLoading(true);
    setError(null);
    try {
      const resp = await verifyCode({ email, code });
      const profile: UserProfile = {
        id: resp.user.id,
        email: resp.user.email,
        name: resp.user.email,
        roles: resp.user.role ? [resp.user.role] : [],
      };
      setApiToken(resp.access_token);
      setUser(profile);
      setIsAuthenticated(true);
      window.localStorage.setItem('db_access_token', resp.access_token);
      window.localStorage.setItem('db_user', JSON.stringify(profile));
    } catch (e: any) {
      setError(e?.message || 'Invalid code or email');
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    apiToken,
    graphToken,
    connectivity,
    lastReachableAt,
    retryConnectivityCheck,
    lastKnownProfileCached,
    login,
    logout,
    acquireGraphTokenSilently,
    acquireApiToken,
    error,
    dbRequestCode,
    dbVerifyAndLogin,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
